import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useMatch } from "@tanstack/react-router";
import { useState } from "react";
import { Toolbar, ToolbarSlot } from "@/client/components/page";
import {
  HeldCount,
  PagePending,
  ShapeSkeleton,
} from "@/client/components/page-pending";
import {
  TeamFrame,
  TeamHeading,
  TeamTabs,
  teamShape,
} from "@/client/components/team-frame";
import { TeamPeoplePending } from "@/client/components/team-people";
import { TeamInvite } from "@/client/components/team-today";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData } from "@/client/lib/session";
import { waitingLeaveQuery } from "@/features/leave/leave-query";
import { peopleQuery } from "@/features/people/people-query";
import { runsTeam } from "@/features/people/roles";

// The frame counts people beside the People tab, and reads Leave's asks to pick both tabs' shape.
export const Route = createFileRoute("/_authenticated/$slug/team")({
  loader: ({ context }) =>
    Promise.all([
      ensureTenantData(context.queryClient, peopleQuery),
      runsTeam(context.role) && context.companyModules.includes("leave")
        ? ensureTenantData(context.queryClient, waitingLeaveQuery)
        : null,
    ]),
  pendingMs: PENDING_MS,
  pendingComponent: TeamPending,
  component: TeamFrame,
});

/**
 * The Team screen's first load: the title, the tabs and the open tab's actions for real. The
 * People count waits on the people, and the shape on the asks unless they were read before.
 */
function TeamPending() {
  const { slug } = Route.useParams();
  const { role, companyModules } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const onPeople = useMatch({
    from: "/_authenticated/$slug/team/people",
    shouldThrow: false,
  });
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const tabs = runsTeam(role);
  const people = queryClient.getQueryData(peopleQuery.queryKey);
  const shape = teamShape({
    tabs,
    attendance: companyModules.includes("attendance"),
    people: people ?? [],
    waiting: queryClient.getQueryData(waitingLeaveQuery.queryKey) ?? [],
  });
  return (
    <PagePending
      shape={shape}
      dated
      header={
        <>
          <TeamHeading />
          {tabs ? (
            <Toolbar
              views={
                <TeamTabs
                  slug={slug}
                  count={
                    people ? (
                      people.filter((row) => row.state === "active").length
                    ) : (
                      <HeldCount />
                    )
                  }
                />
              }
              slot={setSlot}
              inline={!onPeople}
            />
          ) : null}
        </>
      }
    >
      <ToolbarSlot value={slot}>
        {onPeople ? (
          <TeamPeoplePending q={onPeople.search.q} />
        ) : (
          <>
            {tabs ? <TeamInvite /> : null}
            <ShapeSkeleton shape={shape} lines={2} />
          </>
        )}
      </ToolbarSlot>
    </PagePending>
  );
}
